import styled, { DefaultTheme, useTheme } from 'styled-components';
import { SmallBadge } from '../../elements/Badge';
import { PSmall } from '../../elements/Text';
import { SmallTooltip } from '../../Tooltip';
import {
  CompanyResponse,
  LearningPlanModule,
  LearningPlanProject,
} from '../../../api/admin';
import { LEARNING_PLAN_UNIT_TYPE } from '../../LearningPlan/LearningPlanUnit';
import { TOPIC_TO_COLOR } from '../../../theme';
import {
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
} from '../Progress/Tooltip';

export type LearningPlanItem = LearningPlanProject | LearningPlanModule;

export const StatusIcon = styled.i<{ color: string }>`
  font-size: 15px;
  margin: 0;
  padding: 0;
  color: ${(props) => props.color};
`;

export const StatusIconWrapper = ({
  src,
  color,
}: {
  src: string;
  color: string;
}) => {
  return <StatusIcon className={src} color={color} />;
};

export function getStatusIconClass({
  item,
}: {
  item: LearningPlanItem;
}): string {
  if (item.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
    if (item.submittedExercisesCount === item.exercisesCount) {
      return 'fa-solid fa-check';
    }
    if ((item?.submittedExercisesCount ?? 0) > 0) {
      return 'fa-solid fa-circle-quarter-stroke';
    }
    return 'fa-solid fa-play';
  }

  if (item.coreSubmittedLessonsCount === item.coreLessonsCount) {
    return 'fa-solid fa-check';
  }

  if (
    (item.coreSubmittedLessonsCount ?? 0) > 0 ||
    (item?.stretchSubmittedLessonsCount ?? 0) > 0
  ) {
    return 'fa-solid fa-circle-quarter-stroke';
  }

  return 'fa-solid fa-play';
}

export function getStatusTooltip({
  item,
}: {
  item: LearningPlanItem;
}): React.ReactNode {
  if (item.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
    const status = (() => {
      if (item.submittedExercisesCount === item.exercisesCount) {
        return 'Completed';
      }
      if ((item?.submittedExercisesCount ?? 0) > 0) {
        return 'In Progress';
      }
      return 'Not Started';
    })();

    return (
      <TooltipContent>
        <TooltipTitle>{item.topic} Project</TooltipTitle>
        <TooltipDetail>{item.companyName ?? 'Public'}</TooltipDetail>
        <TooltipDetail style={{ marginTop: 8 }}>{status}</TooltipDetail>
        <TooltipDetail>
          {`${item.submittedExercisesCount}/${item.exercisesCount} exercises submitted`}
        </TooltipDetail>
      </TooltipContent>
    );
  }

  if (item.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
    const status = (() => {
      if (item.coreSubmittedLessonsCount === item.coreLessonsCount) {
        return 'Completed';
      }
      if (
        (item?.coreSubmittedLessonsCount ?? 0) > 0 ||
        (item?.stretchSubmittedLessonsCount ?? 0) > 0
      ) {
        return 'In Progress';
      }
      return 'Not Started';
    })();

    const coreProgress = `${item.coreSubmittedLessonsCount}/${item.coreLessonsCount} CORE activities submitted`;
    const stretchProgress = `${item.stretchSubmittedLessonsCount}/${item.stretchLessonsCount} STRETCH activities submitted`;

    return (
      <TooltipContent>
        <TooltipTitle>Content Module</TooltipTitle>
        <TooltipDetail>{item.companyName ?? 'Public'}</TooltipDetail>
        <TooltipDetail style={{ marginTop: 8 }}>{status}</TooltipDetail>
        <TooltipDetail>{coreProgress}</TooltipDetail>
        {(item?.stretchLessonsCount ?? 0) > 0 && (
          <TooltipDetail>{stretchProgress}</TooltipDetail>
        )}
      </TooltipContent>
    );
  }
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 100%;
`;

const BadgeWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const BadgeText = styled(PSmall)`
  text-align: center;
  margin-top: 0;
  padding-bottom: 0;
`;

export default function LearningPlanCell({
  learningPlan,
  learnerId,
}: {
  learnerId: number;
  learningPlan: CompanyResponse['learners'][number]['learningPlan'];
}) {
  const theme = useTheme();

  if ((learningPlan ?? []).length === 0) {
    return (
      <Wrapper>
        <span>-</span>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {(learningPlan ?? []).map((item) => {
        const {
          unitId,
          tooltipId,
          colors,
          tooltipCopy,
          badgeCopy,
          statusIconClass,
        } = getLearningPlanItemStyles({
          item,
          learnerId,
          theme,
        });

        return (
          <BadgeWrapper key={unitId} data-tooltip-id={tooltipId}>
            <SmallBadge
              colors={colors}
              style={{
                width: 'max-content',
              }}
            >
              <Row>
                <StatusIconWrapper
                  src={statusIconClass}
                  color={colors.highlightColor}
                />
                <BadgeText>{badgeCopy}</BadgeText>
              </Row>
            </SmallBadge>

            <SmallTooltip anchorSelect={`[data-tooltip-id="${tooltipId}"]`}>
              {tooltipCopy}
            </SmallTooltip>
          </BadgeWrapper>
        );
      })}
    </Wrapper>
  );
}

export function getLearningPlanItemStyles({
  item,
  learnerId,
  theme,
}: {
  item: LearningPlanItem;
  learnerId: number;
  theme: DefaultTheme;
}) {
  const unitId = getUnitId({ item, learnerId });

  return {
    unitId,
    tooltipId: `learning-plan-${unitId}`,
    colors: getBadgeColors({ item, theme }),
    tooltipCopy: getStatusTooltip({ item }),
    badgeCopy:
      item.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
        ? item.content.title
        : item.title,
    statusIconClass: getStatusIconClass({ item }),
  };
}

export function getUnitId({
  item,
  learnerId,
}: {
  item: LearningPlanItem;
  learnerId: number;
}) {
  return item.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
    ? `${item.content.id}-${item.type}-${learnerId}`
    : `${item.projectId}-${item.type}-${learnerId}`;
}

export function getBadgeColors({
  item,
  theme,
}: {
  item: LearningPlanItem;
  theme: DefaultTheme;
}) {
  const topic = item.topic;
  const topicColor =
    TOPIC_TO_COLOR?.[topic as keyof typeof TOPIC_TO_COLOR] ??
    theme.colors.bgDefault;

  return {
    textColor: theme.colors.textPrimary,
    backgroundColor: topicColor,
    highlightColor: theme.colors.textPrimary,
  };
}
